import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button, Form, RadioLabel, DropdownLight, TooltipHelp, Tooltip, OptionalSection, Section, Message, Icon, DateTimeInput } from '@weezevent/nacre';
import moment from 'moment-timezone';
import { useForm } from '../../utils/validators';
import Styles from '../../styles/eventCreation/eventCreation.module.css';
import i18n from 'i18next';
import timezones from './../../timezones/timezones.json';
import useAppContext from '../../context/useAppContext';
import { useSegment } from '../../components/tracking/trackingProvider';
import SaveAndRedirectButton from './components/saveAndRedirectButton';

function DisplayManagerDateTimeLine({ timezone, form, inventory, label, index, setForm, onDelete }) {
    const currentYear = new Date().getFullYear();
    const locale = i18n.language;
    moment.locale(locale);

    const endDate = new Date(form.end_date);

    const onChange = React.useCallback(
        date => {
            let inv = { ...(form.inventories[index].object || form.inventories[index]) };
            inv.start_date = date;
            form.inventories[index] = inv;
            setForm('inventories', form.inventories);
        },
        [form, index, setForm]
    );

    return (
        <div>
            <div className={Styles['display-manager-date-line']}>
                <div className={Styles['display-manager-date-label']}>{label}</div>
                <div className={Styles['display-manager-date-dates']}>
                    <DateTimeInput
                        disabled={index === 0}
                        className={Styles['display-manager-date-time-input']}
                        onChange={onChange}
                        value={inventory.start_date}
                        dateProps={{
                            minYear: new Date(form.start_date).getFullYear(),
                            minDate: new Date(form.start_date),
                            maxYear: form.end_date ? new Date(form.end_date).getFullYear() : currentYear + 15,
                            maxDate: form.end_date ? endDate.setDate(endDate.getDate() + 1) : null
                        }}
                        locale={locale}
                        timezone={timezone}
                        placeholder={moment(form.start_date)
                            .add(index, 'days')
                            .format(moment.localeData().longDateFormat('l'))}
                    />
                </div>
                <div className={Styles['display-manager-date-icon']}>{index > 0 && !inventory.uuid && <Icon name="trash" onClick={() => onDelete(index)} />}</div>
            </div>
            <div className={Styles['display-manager-date-separator']} />
        </div>
    );
}

function MultiDateEventManager({ form, setForm, timezone, eventErrors }) {
    const { t } = useTranslation();

    const handleAdd = React.useCallback(() => {
        let inventory = form.inventories;
        inventory.push(...(!inventory.length ? [{ uuid: null, name: form.start_date, start_date: form.start_date, index: 0 }] : []), {
            uuid: null,
            name: null,
            start_date: null,
            index: !inventory.length ? 1 : inventory.length
        });
        setForm('inventories', inventory);
    }, [form]);

    const handleDelete = React.useCallback(
        index => {
            let inventory = form.inventories;
            inventory.splice(index, 1);
            setForm('inventories', inventory);
        },
        [form]
    );

    const sortedDates = React.useMemo(() => {
        return form.inventories.sort((a, b) => {
            if (a.start_date >= form.start_date && b.start_date >= form.start_date) {
                return (a.start_date !== null ? new Date(a.start_date) : Infinity) - (b.start_date !== null ? new Date(b.start_date) : Infinity);
            }
            return form.inventories;
        });
    }, [form]);

    const renderDates = React.useMemo(() => {
        if (sortedDates.length < 2) {
            return null;
        }
        return sortedDates.map((inventory, index) => {
            return (
                <DisplayManagerDateTimeLine
                    key={index}
                    form={form}
                    inventory={inventory}
                    label={index === 0 ? t('ticket.event.start_date') : t('ticket.event.day', { count: index + 1 })}
                    index={index}
                    onDelete={handleDelete}
                    timezone={timezone}
                    setForm={setForm}
                />
            );
        });
    }, [sortedDates, form, handleDelete, timezone]);

    return (
        <div className={Styles['multi-date-manager-container']}>
            <div className={Styles['multi-date-manager-line']} />
            <div className={Styles['multi-date-manager-content']}>
                {renderDates}
                <Form.FieldErrors errors={eventErrors['inventories'] && [t(eventErrors['inventories'])]} />
                <Button
                    className={Styles['multi-date-manager-cta']}
                    label={
                        <div>
                            <Icon name="calendar" />
                            {t(`ticket.creationEvent.dates.${form.inventories.length > 0 ? 'add_date' : 'add_first_date'}`)}
                        </div>
                    }
                    onClick={handleAdd}
                />
            </div>
        </div>
    );
}

export default function EventCreationDates({
    onChange,
    onChangeObject,
    onDone = null,
    formEvent,
    isSubmitting,
    onCardDone = () => {},
    edit = false,
    eventUseSessions = false,
    eventErrors = {},
    onSaveAndRedirect = () => {},
    hideSaveAndRedirect = false
}) {
    const { t } = useTranslation();
    const locale = i18n.language;
    let [{ organization, event }] = useAppContext();

    const { track } = useSegment();

    const currentYear = new Date().getFullYear();

    const validateStartDate = React.useCallback((name, date, dateForm) => {
        if (!dateForm.start_date) {
            return 'ticket.event.no_date_start';
        }
        return true;
    }, []);

    const validateDate = React.useCallback((name, date, dateForm) => {
        if (!dateForm.start_date) {
            return;
        }
        let startTzEvent = moment.tz(dateForm.start_date, dateForm.timezone);

        if (dateForm.end_date) {
            let endTzEvent = moment.tz(dateForm.end_date, dateForm.timezone);
            if (startTzEvent.isAfter(endTzEvent)) {
                return 'ticket.event.dateend_before_error';
            }
        }
        return true;
    }, []);

    const validators = React.useMemo(() => {
        return {
            start_date: validateStartDate,
            end_date: validateDate
        };
    }, [validateStartDate, validateDate]);

    const [dateForm, setDate, setDateObject, dateErrors, dateValidate] = useForm(
        {
            start_date: null,
            end_date: null,
            use_sessions: false,
            timezone: formEvent.timezone
        },
        validators
    );

    const currentErrors = !onDone ? eventErrors : dateErrors;

    const _onDone = React.useCallback(() => {
        const isValid = dateValidate();

        if (isValid) {
            onDone(onCardDone);
        }
    }, [onCardDone, onDone, dateValidate]);

    React.useEffect(() => {
        let date = {
            start_date: formEvent.start_date,
            end_date: formEvent.end_date,
            use_sessions: formEvent.use_sessions,
            timezone: formEvent.timezone
        };

        return setDateObject(date);
    }, [formEvent]);

    const onChangeDate = React.useCallback(
        (keyFormEvent, date) => {
            onChange(keyFormEvent, date);
        },
        [onChange]
    );

    const onChangeTimezone = React.useCallback(
        newTimezone => {
            //Update dates with new timezone selected
            if (dateForm.start_date) {
                dateForm.start_date = moment
                    .tz(
                        moment
                            .utc(dateForm.start_date, moment.ISO_8601)
                            .tz(dateForm.timezone)
                            .format('YYYY-MM-DD HH:mm'),
                        newTimezone
                    )
                    .utc()
                    .format();
            }
            if (dateForm.end_date) {
                dateForm.end_date = moment
                    .tz(
                        moment
                            .utc(dateForm.end_date, moment.ISO_8601)
                            .tz(dateForm.timezone)
                            .format('YYYY-MM-DD HH:mm'),
                        newTimezone
                    )
                    .utc()
                    .format();
            }
            dateForm.timezone = newTimezone;
            onChangeObject(dateForm);
        },
        [onChangeObject, dateForm]
    );

    let timezonesOptions = React.useMemo(() => {
        return timezones.map(v => ({ key: v, label: v, value: v }));
    }, [timezones]);

    const toUseSessionsNotAllowed = React.useMemo(() => {
        return Boolean(
            (!formEvent.use_sessions && formEvent.attendees) ||
                (!formEvent.use_sessions && formEvent.chart && formEvent.inventories?.length > 1) ||
                event?.is_passculture_activated ||
                event?.isRealSeatingNoSessions()
        );
    }, [formEvent, event]);

    const showSessionsEditor = Boolean(formEvent.chart && !formEvent.is_seated && !formEvent.use_sessions && formEvent.inventories.length && edit);

    return (
        <>
            <Form.Field style={{ marginBottom: '0' }}>
                <Form.Label label={t('ticket.creationEvent.dates.formLabelType')} />
                <div>
                    <Tooltip
                        className={Styles['tooltip-radio-label-dates']}
                        position={'top-start'}
                        title={formEvent.use_sessions && formEvent.attendees ? t('ticket.event.edit.editNotAvailable') : t('ticket.creationEvent.dates.tooltipWithoutUseSessions')}
                        triggerOnHover
                    >
                        <RadioLabel
                            className={Styles['radio-label-custom']}
                            inline
                            bordered
                            name="WithoutUseSessions"
                            checked={!formEvent.use_sessions}
                            disabled={Boolean(formEvent.use_sessions && (formEvent.attendees || formEvent.inventories?.length > 1))}
                            label={t('ticket.creationEvent.dates.formLabelTypeUnique')}
                            onClick={() => {
                                onChangeObject({
                                    use_sessions: false,
                                    end_date: null
                                });
                                track('ACTION > SELECT DATE TYPE', {
                                    'Date type': 'One',
                                    'Page Title': 'CREATE EVENT'
                                });
                            }}
                        />
                    </Tooltip>

                    <Tooltip
                        className={Styles['tooltip-radio-label-dates']}
                        position={'top-start'}
                        title={!formEvent.use_sessions && formEvent.attendees ? t('ticket.event.edit.editNotAvailable') : t('ticket.creationEvent.dates.tooltipWithUseSessions')}
                        triggerOnHover
                    >
                        <RadioLabel
                            className={Styles['radio-label-custom']}
                            inline
                            bordered
                            disabled={toUseSessionsNotAllowed}
                            name="withUseSessions"
                            checked={formEvent.use_sessions}
                            label={t('ticket.creationEvent.dates.formLabelTypeSessions')}
                            onClick={() => {
                                onChangeObject({
                                    use_sessions: true,
                                    end_date: null
                                });
                                track('ACTION > SELECT DATE TYPE', {
                                    'Date type': 'Sessions',
                                    'Page Title': 'CREATE EVENT'
                                });
                            }}
                        />
                    </Tooltip>
                </div>
            </Form.Field>
            {edit && eventUseSessions && dateForm.use_sessions && (
                <Message className={Styles['message-session-mode']} mini={false} title={t('ticket.event.messageUseSessions.title')} type="default" visible>
                    {t('ticket.event.messageUseSessions.content')}
                    <Link target="_blank" rel="noopener noreferrer" className={Styles['go-to-dates']} to={`/ticket/O${organization.id}/E${formEvent.id}/settings/sessions`}>
                        {t('ticket.event.messageUseSessions.linkManageDates')}
                    </Link>
                </Message>
            )}
            <div className={'container-dates-form'}>
                {(!edit || (edit && !eventUseSessions) || (edit && eventUseSessions && !dateForm.use_sessions)) && (
                    <Form.Field>
                        <div className={Styles['container-dates-input']}>
                            <DateTimeInput
                                className={Styles['date-time-input']}
                                timeLabel={t('ticket.event.start_hour')}
                                dateLabel={
                                    <Form.Label label={formEvent.use_sessions ? t('ticket.creationEvent.dates.formLabelFirstSession') : t('ticket.event.start_date')} required />
                                }
                                onChange={(date, dateObject) => {
                                    setDate('start_date', date);
                                    onChangeDate('start_date', date);
                                    if (formEvent.inventories.length > 0 && formEvent.chart && !formEvent.is_seated && !formEvent.use_sessions) {
                                        const smallestInventoryDate = formEvent.inventories.sort((a, b) => {
                                            return new Date(a.start_date) - new Date(b.start_date);
                                        })[0];
                                        formEvent.inventories.find(inventory => inventory.uuid === smallestInventoryDate.uuid).start_date = date;
                                        onChange('inventories', formEvent.inventories);
                                    }
                                }}
                                value={dateForm.start_date}
                                dateProps={{ minYear: currentYear - 7, maxYear: currentYear + 15 }}
                                locale={locale}
                                timezone={dateForm.timezone}
                                errors={!!currentErrors['start_date']}
                            />
                            <Form.Field className={`${Styles['timezone__fieldblock']} ${currentErrors['start_date'] ? Styles['timezone__fieldblock-with-error'] : ''}`}>
                                <DropdownLight
                                    className={Styles['timezone']}
                                    value={dateForm?.timezone}
                                    onChange={value => {
                                        onChangeTimezone(value);
                                        track('ACTION > SELECT TIMEZONE', {
                                            'Page Title': 'CREATE EVENT'
                                        });
                                    }}
                                    items={timezonesOptions}
                                    placeholder={t('ticket.common.action.select')}
                                />
                            </Form.Field>
                        </div>
                        <Form.FieldErrors errors={currentErrors['start_date'] ? [t(currentErrors['start_date'])] : []} />
                    </Form.Field>
                )}
                {showSessionsEditor && <MultiDateEventManager setForm={onChange} form={formEvent} timezone={dateForm.timezone} eventErrors={eventErrors} />}

                {!edit && formEvent.use_sessions && (
                    <div style={{ marginBottom: '20px', fontSize: '12px' }}>
                        {t('ticket.creationEvent.dates.informationFirstSession')}
                        <TooltipHelp title={t('ticket.creationEvent.dates.informationFirstSessionTooltip')} />
                    </div>
                )}
                {!formEvent.use_sessions && (
                    <div className={Styles['container-end-date']}>
                        <OptionalSection
                            label={t('ticket.creationEvent.dates.formLabelAddDateEnd')}
                            open={Boolean(dateForm?.end_date)}
                            onToggle={display => {
                                track('ACTION > ADD END DATE', {
                                    'Page Title': 'CREATE EVENT',
                                    Open: display
                                });
                            }}
                        >
                            <Section className={`${edit ? Styles['edit-section-date-input-end'] : Styles['section-date-input-end']}`}>
                                <div className={Styles['container-dates-input']}>
                                    <DateTimeInput
                                        timeLabel={t('ticket.event.end_hour')}
                                        dateLabel={t('ticket.event.end_date')}
                                        onChange={(date, dateObject) => {
                                            setDate('end_date', date);
                                            onChangeDate('end_date', date);
                                        }}
                                        value={dateForm.end_date}
                                        dateProps={{ minYear: currentYear - 7, maxYear: currentYear + 15, minDate: dateForm.start_date }}
                                        locale={locale}
                                        timezone={dateForm.timezone}
                                        errors={!!currentErrors['end_date']}
                                        defaultHour={23}
                                        defaultMinute={59}
                                    />
                                </div>
                                <Form.FieldErrors errors={currentErrors['end_date'] ? [t(currentErrors['end_date'])] : []} />
                            </Section>
                        </OptionalSection>
                    </div>
                )}
                {!showSessionsEditor && eventErrors['inventories'] && <Message type="danger" title={t(eventErrors['inventories'])}></Message>}
            </div>
            <div className={Styles['buttons-submit']}>
                {onDone && (
                    <>
                        <Button primary className={Styles['button-continue']} label={t('ticket.common.action.continue')} onClick={_onDone} isLoading={isSubmitting} />
                        <SaveAndRedirectButton key={'dates'} performAction={onSaveAndRedirect} hideButton={hideSaveAndRedirect} loading={isSubmitting} />
                    </>
                )}
            </div>
        </>
    );
}

EventCreationDates.propTypes = {
    onChange: PropTypes.func.isRequired,
    onChangeObject: PropTypes.func.isRequired,
    onDone: PropTypes.func,
    onCardDone: PropTypes.func,
    formEvent: PropTypes.object.isRequired,
    isSubmitting: PropTypes.bool.isRequired,
    children: PropTypes.node,
    edition: PropTypes.bool
};
